import * as libRegions from '@luxuryescapes/lib-regions';

import { brandsByValue } from '~/consts/brands';

export type BrandRegionOverrides = Record<string, Array<string>>;
export type BrandRegionOverridesData = BrandRegionOverrides | null | undefined;

interface BrandRegionOverrideDisplayEntry {
  brand: string;
  brandName: string;
  regions: Array<string>;
}

export function getRegionNameFromCode(regionCode: string): string {
  if (regionCode === 'world') {
    return 'World';
  }

  const region = libRegions.getRegionByCode(regionCode, 'luxuryescapes');
  return region?.name ?? regionCode;
}

export function getDisplayRegions(regionCodes: Array<string>): Array<string> {
  const normalizedRegionCodes = Array.from(new Set((regionCodes ?? []).filter(Boolean)));

  if (normalizedRegionCodes.includes('world')) {
    return ['World'];
  }

  return normalizedRegionCodes
    .map((regionCode) => getRegionNameFromCode(regionCode))
    .sort((firstRegion, secondRegion) => firstRegion.localeCompare(secondRegion));
}

function normalizeRegionCodes(regionCodes: Array<string>): Array<string> {
  const dedupedCodes = Array.from(new Set((regionCodes ?? []).filter(Boolean)));

  if (dedupedCodes.includes('world')) {
    return ['world'];
  }

  return dedupedCodes
    .filter((regionCode) => Boolean(libRegions.getRegionByCode(regionCode, 'luxuryescapes')))
    .sort((firstRegion, secondRegion) => firstRegion.localeCompare(secondRegion));
}

export function normalizeBrandRegionOverrides(
  brandRegionOverrides: BrandRegionOverridesData,
): BrandRegionOverrides | null {
  if (!brandRegionOverrides || typeof brandRegionOverrides !== 'object') {
    return null;
  }

  const normalizedOverrides: BrandRegionOverrides = {};

  Object.entries(brandRegionOverrides).forEach(([brand, regions]) => {
    if (!brand || !brandsByValue[brand]) {
      return;
    }

    const normalizedRegions = normalizeRegionCodes(regions ?? []);
    if (!normalizedRegions.length) {
      return;
    }

    normalizedOverrides[brand] = normalizedRegions;
  });

  return Object.keys(normalizedOverrides).length ? normalizedOverrides : null;
}

export function getBrandRegionOverridesDisplay(
  brandRegionOverrides: BrandRegionOverridesData,
): Array<BrandRegionOverrideDisplayEntry> {
  if (!brandRegionOverrides || typeof brandRegionOverrides !== 'object') {
    return [];
  }

  return Object.entries(brandRegionOverrides)
    .map(([brand, regions]) => {
      return {
        brand,
        brandName: brandsByValue[brand]?.title ?? brand,
        regions: getDisplayRegions(regions ?? []),
      };
    })
    .filter(({ brand, regions }) => Boolean(brand) && regions.length > 0)
    .sort((firstEntry, secondEntry) => {
      const brandNameSortResult = firstEntry.brandName.localeCompare(secondEntry.brandName);

      if (brandNameSortResult !== 0) {
        return brandNameSortResult;
      }

      return firstEntry.brand.localeCompare(secondEntry.brand);
    });
}
